import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { Backdrop, Box, Button, CircularProgress, Grid } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { RootState } from "../../../redux/store";
import {
  CAData,
  fetchClientData,
  fetchClientDataRepresentant,
} from "../slices/clientSlice";
import { CustomInfos, CustomLabel, LoadingInfos } from "../components";
import svg2 from "../assets/2.svg";
import svg3 from "../assets/3.svg";
import svg11 from "../assets/11.svg";
import { useTranslation } from "react-i18next";
import { ReleveData, downloadReleve } from "../slices/downloadReleveSlice";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { IsAuthorizedRoles } from "../../../components/auth";
import Roles from "../../../constants/roles";

// Noms des mois en français
const moisNoms = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

interface PdfColumn {
  header: string;
  dataKey: keyof ReleveData;
}

// Formate le mois en texte
const formaterMois = (mois: number) => moisNoms[mois - 1];

const InfosClientPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { clientInfo, status, error } = useAppSelector(
    (state: RootState) => state.clientSlice
  );
  const { numClient } = useAppSelector(
    (state: RootState) => state.listClientSlice
  );
  const { direction } = useAppSelector(
    (state: RootState) => state.languageSlice
  );
  const { roles } = useAppSelector((state: RootState) => state.authSlice);
  const currentYear = new Date().getFullYear();
  const [baseYear, setBaseYear] = useState<number>(currentYear);
  const { t } = useTranslation();
  const { loading: downloadLoading } = useAppSelector(
    (state) => state.downloadReleveSlice
  );

  useEffect(() => {
 
    if (roles.includes("Representant")) {
      if (numClient) {
        dispatch(fetchClientDataRepresentant({ NumeroClient: numClient })).then(
          (result) => {
            if (fetchClientDataRepresentant.fulfilled.match(result)) {

            } else if (fetchClientDataRepresentant.rejected.match(result)) {
              console.error(
                "Erreur lors de la récupération des données du client représentant:",
                result.payload || result.error.message
              );
            }
          }
        );
      } else {
        console.error(
          "Numéro de client manquant pour la requête de données représentant."
        );
      }
    } else {
      dispatch(fetchClientData()).then((result) => {
        if (fetchClientData.fulfilled.match(result)) {
        } else if (fetchClientData.rejected.match(result)) {
          console.error(
            "Erreur lors de la récupération des données client:",
            result.payload || result.error.message
          );
        }
      });
    }
  }, [dispatch, roles, numClient]);

  const toutesLesAnnees = clientInfo
    ? Array.from(new Set(clientInfo.CAdata.map((item) => item.ANNEE)))
    : [];

  useEffect(() => {
    if (toutesLesAnnees.length > 0 && baseYear === currentYear) {
      setBaseYear(Number(toutesLesAnnees[0]));
    }
  }, [toutesLesAnnees, baseYear, currentYear]);

  const transformerData = (data: CAData[], baseYear: number) => {
    const anneesInclure = [
      baseYear.toString(),
      (baseYear - 1).toString(),
      (baseYear - 2).toString(),
    ];
    const dataFiltre = data.filter((item) =>
      anneesInclure.includes(item.ANNEE)
    );

    const groupedData: { [key: string]: any } = {};

    dataFiltre.forEach((item) => {
      const moisKey = formaterMois(Number(item.MOIS));
      if (!groupedData[moisKey]) {
        groupedData[moisKey] = { mois: moisKey };
      }
      groupedData[moisKey][item.ANNEE] = Number(item.CA);
    });

    return Object.values(groupedData);
  };

  const dataGraphique = clientInfo
    ? transformerData(clientInfo.CAdata, baseYear)
    : [];

  const handleDownloadReleve = () => {
    if (clientInfo) {
      dispatch(downloadReleve(clientInfo.NumeroClient)).then((result) => {
        if (downloadReleve.fulfilled.match(result)) {
          const releveData: ReleveData[] = result.payload;
          const doc = new jsPDF();

          const columns: PdfColumn[] = [
            { header: "Reference", dataKey: "reference" },
            { header: "Echeance", dataKey: "echeance" },
            { header: "Exigible", dataKey: "exigible" },
          ];
          const rows = releveData.map((item) => ({
            reference: item.reference,
            echeance: new Date(item.echeance).toLocaleDateString(),
            exigible: item.exigible,
          }));

          (doc as any).autoTable({
            head: [columns.map((col) => col.header)],
            body: rows.map((row) => columns.map((col) => row[col.dataKey])),
          });

          doc.save("releve.pdf");

        } else if (downloadReleve.rejected.match(result)) {
          console.error(
            "Erreur lors du téléchargement du relevé:",
            result.payload || result.error.message
          );
        }
      });
    }
  };

  return (
    <Box
      p={5}
      sx={{
        border: "0.5px solid #F9F9F9",
        boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
        borderRadius: "10px",
      }}
    >
      {status === "loading" && <LoadingInfos />}
      {status === "failed" && <p>{error}</p>}
      {status === "succeeded" && clientInfo && (
        <>
          <Grid
            container
            mb={3}
            sx={{
              direction: direction === "rtl" ? "rtl" : "ltr",
            }}
          >
            <Grid
              item
              xs
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <CustomLabel
                label={t("detailOrder.number")}
                value={clientInfo.NumeroClient}
              />
            </Grid>
            <IsAuthorizedRoles roles={[Roles.REPRESENTATIVE]}>
              <Grid
                item
               
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  variant="contained"
                  disableElevation
                  sx={{ width: "auto" }}
                  onClick={handleDownloadReleve}
                >
                  {t("detailOrder.downloadReleve")}
                </Button>
                <Backdrop
                  sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                  }}
                  open={downloadLoading}
                >
                  <CircularProgress color="inherit" />
                </Backdrop>
              </Grid>
            </IsAuthorizedRoles>
            <Grid
              xs
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <CustomLabel
                label={t("detailOrder.nom")}
                value={clientInfo.RaisonSociale}
              />
            </Grid>
          </Grid>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={dataGraphique}>
              <CartesianGrid strokeDasharray="3 3" />
              <YAxis type="number" domain={["auto", "auto"]} />
              <XAxis dataKey="mois" type="category" reversed />
              <Tooltip />
              <Legend />
              <Bar dataKey={baseYear.toString()} fill="#8884d8" />
              <Bar dataKey={(baseYear - 1).toString()} fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
          <Box mt={2}>
            <CustomInfos
              iconPath={svg2}
              backgroundColor="#fff"
              color="#000"
              text={t("detailOrder.ongoingOrders")}
              value={clientInfo.SituationEnCommande}
            />
            <CustomInfos
              iconPath={svg3}
              backgroundColor="#fff"
              color="#000"
              text={t("detailOrder.deliveredOrders")}
              value={clientInfo.SituationEnLivraison}
            />
            <CustomInfos
              iconPath={svg11}
              backgroundColor="#C4DBFB"
              color="#000"
              text={t("detailOrder.situation")}
              value={clientInfo.SoldeClient}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default InfosClientPage;
